/**
 * Shape tokens — corner radii mirrored from Figma Variables (collection "base/sizes/radius").
 *
 * `shape.borderRadius` feeds MUI's `theme.shape.borderRadius` — the single default radius
 *   consumed by most MUI components (inputs, buttons, Paper, chips…). Set to rd-4 (4px).
 *
 * `radius` provides all named steps for use in component `styleOverrides` when a component
 *   needs a radius other than the default.
 */
export const shape = {
  borderRadius: 4, // rd-4 — default corner radius
} satisfies { borderRadius: number };

export const radius = {
  none: 0, // rd-none — sharp corners
  xs: 2, // rd-2   — subtle, hairline (e.g. small badges)
  sm: 4, // rd-4   — default (= shape.borderRadius: inputs, buttons, cards)
  rd6: 6, // rd-6   — one step above the default, below the modal radius. See the naming note.
  md: 8, // rd-8   — modals, dropdowns, tooltips
  pill: 40, // rd-40  — pill buttons, chips, tags
} as const;

/*
 * Naming note (2026-08-26): `rd6` breaks the t-shirt pattern on purpose. The scale is a mirror of
 * Figma's `rd-*` variables, and `rd-6` lands between `sm` (rd-4) and `md` (rd-8) — there is no free
 * t-shirt slot to insert it into without renaming the steps above it, which would touch every
 * component that references them. Keeping the Figma name is also consistent with how the two
 * sibling token files already name their steps (`spacingTokens.sp6`, `sizes.height.h4`).
 * If we want one convention across all of `radius`, the clean fix is to migrate the whole object to
 * literal names (rd2/rd4/rd6/rd8/rd40) in a dedicated mechanical pass — designer's call.
 */

export type ShapeTokens = typeof shape;
export type RadiusTokens = typeof radius;
